import PageToolbar from 'ui/PageToolbar/PageToolbar';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { Table, Tag, Typography, Button, Input, Tooltip, Alert } from 'antd';
import {
  SearchOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  ScissorOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { ColumnsType, TableProps } from 'antd/es/table';
import { SortOrder } from 'antd/es/table/interface';
import { StreamOrderBy } from 'api/graphql-global-types';
import { AIPpvClipListItem } from 'api/aiPpvClips/types';
import {
  fetchPpvClipRecordsPage,
  fetchAllPpvClipRecordsByNameSearch,
  FetchPpvClipRecordsPageResult,
} from 'api/aiPpvClips/client';
import { AI_PPV_CLIP_DETAIL_GEN } from 'constants/routes';
import styles from '../AIClips/AIClips.module.scss';

const { Text } = Typography;

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_SORT_DIRECTION = 'DESC';
/**
 * Cast until apollo codegen picks the member up, which needs the API deployed first. The literal
 * is the GraphQL enum name, which is what goes over the wire — not the TS enum's value.
 */
const AI_CLIPS_CREATED_AT = 'AiClipsCreatedAt' as StreamOrderBy;

const DEFAULT_ORDER_BY = AI_CLIPS_CREATED_AT;

/** Sorted by the API: the table is paginated, so sorting locally would reorder 20 rows of N. */
const SORTABLE_COLUMNS: Record<string, StreamOrderBy> = {
  storeName: StreamOrderBy.StoreName,
  streamName: StreamOrderBy.Name,
  streamCreatedAt: StreamOrderBy.ScheduleDateStrict,
  clipsCreatedAt: AI_CLIPS_CREATED_AT,
};

/**
 * Repeat the first direction so Ant Design 4 wraps instead of emitting cancel. Its cycle is
 * `sortDirections[indexOf(current) + 1]`; the last entry would otherwise be undefined.
 */
const SORT_DIRECTIONS: SortOrder[] = ['descend', 'ascend', 'descend'];

type NameSearchCache = {
  key: string;
  records: AIPpvClipListItem[];
};

function getSearchConfig(searchTerm: string): {
  key: string;
  isNameSearch: boolean;
  params: Record<string, string>;
} {
  const trimmed = searchTerm.trim();
  const isUuidLike = /^[0-9a-f-]{8,36}$/i.test(trimmed);
  const isSlugLike =
    !isUuidLike && /^[a-z0-9]+(?:[.\-_][a-z0-9]+)+$/i.test(trimmed);

  if (!trimmed) {
    return { key: '', isNameSearch: false, params: {} };
  }

  if (isUuidLike) {
    return {
      key: `id:${trimmed.toLowerCase()}`,
      isNameSearch: false,
      params: { id: trimmed },
    };
  }

  if (isSlugLike) {
    return {
      key: `slug:${trimmed.toLowerCase()}`,
      isNameSearch: false,
      params: { slugSearch: trimmed },
    };
  }

  return {
    key: `name:${trimmed.toLowerCase()}`,
    isNameSearch: true,
    params: {
      storeName: trimmed,
      streamName: trimmed,
    },
  };
}

const AIPpvClips: React.FC = () => {
  const history = useHistory();
  const apolloClient = useApolloClient();
  const [records, setRecords] = useState<AIPpvClipListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [orderBy, setOrderBy] = useState<StreamOrderBy>(DEFAULT_ORDER_BY);
  const [direction, setDirection] = useState<'ASC' | 'DESC'>(
    DEFAULT_SORT_DIRECTION
  );
  const nameSearchCacheRef = useRef<NameSearchCache | null>(null);

  const load = useCallback(
    async (
      currentPage: number,
      size: number,
      searchTerm: string,
      sortBy: StreamOrderBy,
      sortDirection: 'ASC' | 'DESC',
      forceRefresh = false
    ) => {
      setLoading(true);
      setError(null);
      try {
        const searchConfig = getSearchConfig(searchTerm);

        if (searchConfig.isNameSearch) {
          let cachedRecords = nameSearchCacheRef.current?.records;
          const isSameSearch =
            nameSearchCacheRef.current?.key === searchConfig.key;

          if (!isSameSearch || forceRefresh || !cachedRecords) {
            cachedRecords = await fetchAllPpvClipRecordsByNameSearch(
              apolloClient,
              searchTerm,
              DEFAULT_SORT_DIRECTION
            );
            nameSearchCacheRef.current = {
              key: searchConfig.key,
              records: cachedRecords,
            };
          }

          const startIndex = (currentPage - 1) * size;
          setRecords(cachedRecords.slice(startIndex, startIndex + size));
          setTotal(cachedRecords.length);
          return;
        }

        const data: FetchPpvClipRecordsPageResult =
          await fetchPpvClipRecordsPage(apolloClient, {
            limit: size,
            offset: (currentPage - 1) * size,
            direction: sortDirection,
            orderBy: sortBy,
            ...searchConfig.params,
          });
        nameSearchCacheRef.current = null;
        setRecords(data.entities);
        setTotal(data.total);
      } catch (e: any) {
        setError(e?.message || 'Failed to load clips from the API.');
      } finally {
        setLoading(false);
      }
    },
    [apolloClient]
  );

  useEffect(() => {
    load(page, pageSize, appliedSearch, orderBy, direction);
  }, [load, page, pageSize, appliedSearch, orderBy, direction]);

  const handleSearch = useCallback(() => {
    setAppliedSearch(searchInput);
    setPage(1);
  }, [searchInput]);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') handleSearch();
    },
    [handleSearch]
  );

  const sortOrderFor = useCallback(
    (columnKey: string): SortOrder | undefined => {
      if (SORTABLE_COLUMNS[columnKey] !== orderBy) return undefined;
      return direction === 'ASC' ? 'ascend' : 'descend';
    },
    [orderBy, direction]
  );

  const handleTableChange = useCallback<
    NonNullable<TableProps<AIPpvClipListItem>['onChange']>
  >(
    (_pagination, _filters, sorter, extra) => {
      if (extra.action !== 'sort') return;

      const { columnKey, order } = Array.isArray(sorter) ? sorter[0] : sorter;
      const nextOrderBy = SORTABLE_COLUMNS[String(columnKey)];
      if (!nextOrderBy) return;

      if (!order) {
        if (nextOrderBy === orderBy) {
          setDirection((current) => (current === 'ASC' ? 'DESC' : 'ASC'));
          setPage(1);
        }
        return;
      }

      setOrderBy(nextOrderBy);
      setDirection(order === 'ascend' ? 'ASC' : 'DESC');
      setPage(1);
    },
    [orderBy]
  );

  const columns: ColumnsType<AIPpvClipListItem> = [
    {
      title: 'Store',
      dataIndex: 'storeName',
      key: 'storeName',
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      sortOrder: sortOrderFor('storeName'),
      render: (_: unknown, row: AIPpvClipListItem) => (
        <Text strong>{row.storeName || <Text type="secondary">—</Text>}</Text>
      ),
    },
    {
      title: 'Stream',
      dataIndex: 'streamName',
      key: 'streamName',
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      sortOrder: sortOrderFor('streamName'),
      render: (_: unknown, row: AIPpvClipListItem) => (
        <Text strong>{row.streamName || <Text type="secondary">—</Text>}</Text>
      ),
    },
    {
      title: 'Stream ID',
      dataIndex: 'streamId',
      key: 'streamId',
      ellipsis: true,
      render: (id: string) => (
        <span className={styles.copyableCell}>
          <Tooltip title={id}>
            <Text code copyable={{ text: id }}>
              {id.length > 16 ? `${id.slice(0, 16)}…` : id}
            </Text>
          </Tooltip>
        </span>
      ),
    },
    {
      title: 'Stream date',
      dataIndex: 'streamCreatedAt',
      key: 'streamCreatedAt',
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      sortOrder: sortOrderFor('streamCreatedAt'),
      render: (date?: string) =>
        date ? (
          <Text type="secondary">{new Date(date).toLocaleString()}</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Clips',
      key: 'clipsGenerated',
      width: 80,
      align: 'center',
      render: (_: unknown, row: AIPpvClipListItem) => (
        <Tag color="blue" icon={<ScissorOutlined />}>
          {row.clipsGenerated}
        </Tag>
      ),
    },
    {
      title: 'Recordings',
      key: 'recordingCount',
      width: 100,
      align: 'center',
      // More than one means the stream was interrupted and was clipped per recording.
      render: (_: unknown, row: AIPpvClipListItem) =>
        row.recordingCount > 1 ? (
          <Tag color="purple">{row.recordingCount} parts</Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Clip status',
      key: 'status',
      width: 120,
      render: (_: unknown, row: AIPpvClipListItem) =>
        row.clipStatus ? (
          <Tag color={row.clipStatus === 'success' ? 'green' : 'orange'}>
            {row.clipStatus.toUpperCase()}
          </Tag>
        ) : (
          <Tag color="default">Missing</Tag>
        ),
    },
    {
      title: 'Clips added',
      key: 'clipsCreatedAt',
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      sortOrder: sortOrderFor('clipsCreatedAt'),
      render: (_: unknown, row: AIPpvClipListItem) =>
        row.processedAt ? (
          <Text type="secondary">
            {new Date(row.processedAt).toLocaleString()}
          </Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 130,
      render: (_: unknown, row: AIPpvClipListItem) =>
        row.recordingCount > 0 && row.userSlug && row.streamSlug ? (
          <Button
            type="primary"
            size="small"
            icon={<PlayCircleOutlined />}
            onClick={() =>
              history.push(
                `${AI_PPV_CLIP_DETAIL_GEN}/${encodeURIComponent(
                  row.userSlug
                )}/${encodeURIComponent(row.streamSlug)}`
              )
            }
          >
            Review
          </Button>
        ) : (
          <Tag color="default">No clip</Tag>
        ),
    },
  ];

  return (
    <div className={styles.container}>
      <PageToolbar
        leading={
          <Text type="secondary">
            Search by store name, stream name, slug, or stream ID.
          </Text>
        }
      >
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search by store, stream, slug, or stream ID…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          allowClear
          style={{ width: 320, maxWidth: '100%' }}
        />
        <Button onClick={handleSearch} loading={loading}>
          Search
        </Button>
        <Button
          icon={<ClearOutlined />}
          onClick={() => {
            setSearchInput('');
            setAppliedSearch('');
            setPage(1);
            nameSearchCacheRef.current = null;
          }}
          disabled={!searchInput && !appliedSearch}
        >
          Clear
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={() =>
            load(page, pageSize, appliedSearch, orderBy, direction, true)
          }
          loading={loading}
        >
          Refresh
        </Button>
      </PageToolbar>

      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Table<AIPpvClipListItem>
        rowKey={(r) => `${r.storeId}__${r.streamId}`}
        columns={columns}
        dataSource={records}
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          onChange: (newPage, newPageSize) => {
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
              setPage(1);
            } else {
              setPage(newPage);
            }
          },
          showQuickJumper: false,
        }}
        scroll={{ x: 1100 }}
        onChange={handleTableChange}
        onRow={(row) => ({
          onClick: (event) => {
            const target = event.target as HTMLElement;
            if (target.closest(`.${styles.copyableCell}`)) {
              return;
            }
            if (row.recordingCount > 0 && row.userSlug && row.streamSlug) {
              history.push(
                `${AI_PPV_CLIP_DETAIL_GEN}/${encodeURIComponent(
                  row.userSlug
                )}/${encodeURIComponent(row.streamSlug)}`
              );
            }
          },
          className: styles.tableRow,
        })}
      />
    </div>
  );
};

export default AIPpvClips;
